import { useEffect, useRef } from 'react';

/**
 * **휴대폰 뒤로 가기(브라우저 뒤로)를 사이트 안에서 받는다.**
 *
 * 사이트는 주소를 바꾸지 않는 한 화면짜리 앱이라, 방문 기록에 남는 게 없어 뒤로 가기를 누르면 사이트가 통째로 닫혔다(사용자).
 * 그래서 창·단계가 열릴 때마다 방문 기록에 **칸을 하나씩** 쌓고, 뒤로 가기가 칸을 걷어내면(popstate) **가장 나중에 열린 것**을
 * 닫는다. 창을 X 버튼으로 닫으면 그 칸도 걷어낸다(`history.back()`, 그 popstate 는 무시).
 *
 * **크롬의 뒤로 가기 건너뛰기 함정(실측: 첫 판은 휴대폰에서 그대로 사이트가 닫혔다).** 크롬은 사용자가 페이지를 만지지 않은 채
 * 사이트가 `pushState` 로 넣은 칸을 뒤로 가기 때 건너뛴다(history manipulation intervention) — 페이지가 뜨자마자 보초를 깔았더니
 * 뒤로 가기가 그 칸째 건너뛰어 사이트를 떠났다. 그래서 **칸은 사용자가 페이지를 만진 뒤에만 넣는다**
 * (`navigator.userActivation.hasBeenActive`, 없으면 첫 터치·키 입력). 창은 대개 눌러서 열리니 열릴 때 바로 넣을 수 있고,
 * 그 전에 열린 것(주소로 바로 들어온 탭 등)은 첫 터치 때 몰아서 넣는다.
 *
 * 맨 아래에는 **바닥 칸** 하나를 둔다 — 닫을 게 없는 첫 화면에서 뒤로 가기가 오면 바로 나가지 않고 "한 번 더 누르면 나갑니다",
 * 2초 안에 또 누르면 그때 나간다.
 */

type Entry = { id: number; back: () => void; pushed: boolean };
const stack: Entry[] = [];
let seq = 0;
let installed = false;
let baseGuard = false;      // 바닥 칸이 깔려 있나
let swallow = 0;            // 우리가 부른 history.back() 이 낼 popstate 수 — 사용자 뒤로 가기가 아니다
let exitArmedAt = 0;
let touched = false;

const hasTouched = () => touched || !!(navigator as any).userActivation?.hasBeenActive;

function push(tag: string) {
    window.history.pushState({ sw: tag }, '', window.location.href);
}

/** 사용자가 만졌으면 모자란 칸을 채운다: 바닥 칸 + 아직 칸이 없는 열린 창들 */
function fill() {
    if (!hasTouched()) return;
    if (!baseGuard) { push('base'); baseGuard = true; }
    for (const e of stack) if (!e.pushed) { push('layer'); e.pushed = true; }
}

function toast(text: string) {
    const el = document.createElement('div');
    el.textContent = text;
    Object.assign(el.style, {
        position: 'fixed', left: '50%', bottom: '72px', transform: 'translateX(-50%)', zIndex: '99999',
        background: 'rgba(30, 27, 75, 0.88)', color: '#fff', padding: '10px 18px', borderRadius: '999px',
        fontSize: '14px', fontWeight: '700', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', pointerEvents: 'none',
        transition: 'opacity .3s ease', opacity: '1'
    } as CSSStyleDeclaration);
    document.body.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; }, 1600);
    setTimeout(() => el.remove(), 2000);
}

/** 앱이 뜰 때 한 번 — 뒤로 가기를 받기 시작한다(칸은 사용자가 만진 뒤에 깐다) */
export function installBackGuard() {
    if (installed) return;
    installed = true;
    const onTouch = () => { touched = true; fill(); };
    window.addEventListener('pointerdown', onTouch, true);
    window.addEventListener('keydown', onTouch, true);
    fill();

    window.addEventListener('popstate', () => {
        if (swallow > 0) { swallow -= 1; return; }
        // 칸이 있는 창 중 가장 나중 것을 닫는다
        for (let i = stack.length - 1; i >= 0; i--) {
            const e = stack[i];
            if (!e.pushed) continue;
            e.pushed = false;            // 칸은 방금 걷혔다 — 닫힐 때 또 걷지 않게
            e.back();
            return;
        }
        // 바닥 칸이 걷혔다 — 첫 화면
        baseGuard = false;
        if (Date.now() - exitArmedAt < 2000) {
            window.history.back();
            return;
        }
        exitArmedAt = Date.now();
        toast('한 번 더 누르면 사이트를 나갑니다');
        fill();
    });
}

/**
 * `active` 인 동안 뒤로 가기가 오면 `onBack` 을 부른다. 여러 개가 열려 있으면 가장 나중에 열린(active 가 된) 것부터.
 * `onBack` 은 매번 새로 넘겨도 된다(마지막 것을 쓴다).
 */
export function useBackHandler(active: boolean, onBack: () => void) {
    const ref = useRef(onBack);
    ref.current = onBack;
    /**
     * 닫힘을 한 박자 미룬다 — React StrictMode(개발 서버)는 효과를 '열기 → 닫기 → 다시 열기'로 한 번 더 돌린다.
     * 닫기에서 바로 history.back() 을 부르면 그 뒤로 가기가 다시 열기의 pushState 보다 늦게 처리돼 칸 수가 어긋나고,
     * 몹을 고르고 창이 닫힐 때 한 칸 더 물러나 사이트를 떠났다(사용자 2026-10-28 "몹을 선택하면 뒤로가기 되버려" — 로컬).
     * 곧바로 다시 열리면 같은 칸을 그대로 이어 쓴다.
     */
    const closing = useRef<{ entry: Entry; timer: ReturnType<typeof setTimeout> } | null>(null);
    useEffect(() => {
        if (!active) return;
        let entry: Entry;
        if (closing.current) {
            clearTimeout(closing.current.timer);
            entry = closing.current.entry;
            closing.current = null;
        } else {
            entry = { id: ++seq, back: () => ref.current(), pushed: false };
        }
        stack.push(entry);
        fill();
        return () => {
            const i = stack.indexOf(entry);
            if (i >= 0) stack.splice(i, 1);
            // X 버튼 등으로 닫혔다 — 이 창 몫의 칸을 걷어낸다(칸은 이름이 없으니 맨 위 것을 걷어도 수가 맞는다)
            const timer = setTimeout(() => {
                closing.current = null;
                if (entry.pushed) {
                    entry.pushed = false;
                    swallow += 1;
                    window.history.back();
                }
            }, 0);
            closing.current = { entry, timer };
        };
    }, [active]);
}
